import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { imeiAPI } from '../api/client'
import ImeiVerifyReport from './ImeiVerifyReport'
import type { ImeiHubServiceId } from './ImeiHubSignalIcon'
import type { ImeiCheckPayload } from '../utils/imeiReportModel'
import { resolveImeiForMaskedReport } from '../utils/imeiReportImeiCache'

type ImeiReport = {
  id: number
  service: string
  imei_masked: string
  is_valid: boolean
  is_stolen: boolean | null
  brand: string | null
  model: string | null
  created_at: string
}

type Props = {
  token: string
  onBrowseHub?: () => void
}

function toPayload(row: ImeiReport): ImeiCheckPayload {
  return {
    is_valid: row.is_valid,
    is_stolen: row.is_stolen,
    brand: row.brand,
    model: row.model,
  }
}

function isHubService(value: string): value is ImeiHubServiceId {
  return [
    'verify',
    'passport',
    'lock',
    'carrier',
    'safecheck',
    'business',
    'android',
  ].includes(value)
}

export default function ImeiReportsPanel({ token, onBrowseHub }: Props) {
  const { t } = useTranslation()
  const [reports, setReports] = useState<ImeiReport[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeId, setActiveId] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    void imeiAPI
      .listReports(token)
      .then((res) => {
        if (cancelled) return
        setReports((res.data as ImeiReport[]) ?? [])
      })
      .catch(() => {
        if (!cancelled) setError(t('vc.imeiHub.result.error'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token, t])

  useEffect(() => {
    function reload() {
      void imeiAPI
        .listReports(token)
        .then((res) => setReports((res.data as ImeiReport[]) ?? []))
        .catch(() => {})
    }
    window.addEventListener('redevicer:imei-report-claimed', reload)
    return () => window.removeEventListener('redevicer:imei-report-claimed', reload)
  }, [token])

  const active = reports.find((row) => row.id === activeId) ?? null

  useEffect(() => {
    if (!active) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setActiveId(null)
    }
    document.addEventListener('keydown', onKeyDown)
    const prevOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.documentElement.style.overflow = prevOverflow
    }
  }, [active])

  if (loading) {
    return <p className="meta-text">{t('common.loading')}</p>
  }

  if (error) {
    return (
      <p className="vc-imei-reports__error" role="alert">
        {error}
      </p>
    )
  }

  if (reports.length === 0) {
    if (!onBrowseHub) return null
    return (
      <div className="vc-reports-empty-action">
        <button type="button" className="vc-imei-report-empty__cta" onClick={onBrowseHub}>
          {t('vc.imeiReport.emptyCta')}
        </button>
      </div>
    )
  }

  return (
    <>
      <ul className="vc-imei-reports">
        {reports.map((row) => (
          <li key={row.id}>
            <ImeiVerifyReport
              variant="compact"
              service={isHubService(row.service) ? row.service : 'verify'}
              data={toPayload(row)}
              imeiMasked={row.imei_masked}
              checkedAt={row.created_at}
              token={token}
              onOpen={() => setActiveId(row.id)}
            />
          </li>
        ))}
      </ul>

      {active
        ? createPortal(
            <div className="vc-imei-report-modal" role="dialog" aria-modal="true">
              <button
                type="button"
                className="vc-imei-report-modal__backdrop"
                aria-label={t('vc.imeiReport.modalClose')}
                onClick={() => setActiveId(null)}
              />
              <div className="vc-imei-report-modal__panel">
                <ImeiVerifyReport
                  variant="full"
                  service={isHubService(active.service) ? active.service : 'verify'}
                  data={toPayload(active)}
                  imeiMasked={active.imei_masked}
                  checkedAt={active.created_at}
                  token={token}
                  imeiDigits={resolveImeiForMaskedReport(active.imei_masked, active.created_at)}
                  onClose={() => setActiveId(null)}
                  onNewCheck={() => setActiveId(null)}
                />
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
